<template>
  <div class="input-text-wrapper">
    <div class="input-text-container">
      <input
        ref="input"
        :class="inputClasses"
        :type="computedType"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="isDisabled"
        :readonly="isReadOnly"
        :maxlength="limit"
        v-bind="$attrs"
        @input="handleInput"
        @change="handleChange"
        @focus="handleFocus"
        @blur="handleBlur"
      />

      <button
        v-if="type === 'password'"
        type="button"
        class="input-text__toggle"
        @click="togglePasswordVisibility"
        :disabled="isDisabled"
      >
        <svg v-if="showPassword" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.5799 11.9999C15.5799 13.9799 13.9799 15.5799 11.9999 15.5799C10.0199 15.5799 8.41992 13.9799 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C13.9799 8.41992 15.5799 10.0199 15.5799 11.9999Z" stroke="#6171E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M11.9998 20.2697C15.5298 20.2697 18.8198 18.1897 21.1098 14.5897C22.0098 13.1797 22.0098 10.8097 21.1098 9.39973C18.8198 5.79973 15.5298 3.71973 11.9998 3.71973C8.46984 3.71973 5.17984 5.79973 2.88984 9.39973C1.98984 10.8097 1.98984 13.1797 2.88984 14.5897C5.17984 18.1897 8.46984 20.2697 11.9998 20.2697Z" stroke="#6171E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14.5299 9.46992L9.46992 14.5299C8.81992 13.8799 8.41992 12.9899 8.41992 11.9999C8.41992 10.0199 10.0199 8.41992 11.9999 8.41992C12.9899 8.41992 13.8799 8.81992 14.5299 9.46992Z" stroke="#6171E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M17.8198 5.77047C16.0698 4.45047 14.0698 3.73047 11.9998 3.73047C8.46984 3.73047 5.17984 5.81047 2.88984 9.41047C1.98984 10.8205 1.98984 13.1905 2.88984 14.6005C3.67984 15.8405 4.59984 16.9105 5.59984 17.7705" stroke="#6171E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M8.41992 19.5297C9.55992 20.0097 10.7699 20.2697 11.9999 20.2697C15.5299 20.2697 18.8199 18.1897 21.1099 14.5897C22.0099 13.1797 22.0099 10.8097 21.1099 9.39969C20.7799 8.87969 20.4199 8.38969 20.0499 7.92969" stroke="#6171E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M15.5104 12.7002C15.2504 14.1102 14.1004 15.2602 12.6904 15.5202" stroke="#6171E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M9.47 14.5303L2 22.0003" stroke="#6171E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M22.0003 2L14.5303 9.47" stroke="#6171E4" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <div v-if="hasError" class="input-text--error">
      <div v-for="(error, index) in errorMessages" :key="index">
        {{ error }}
      </div>
    </div>
  </div>
</template>

<script>
import { clsx } from 'clsx';
import { numberMask, patternMask } from '@/shared/lib/form';

export default {
  inheritAttrs: false,
  model: {
    prop: 'value',
    event: 'input'
  },
  props: {
    value: {
      type: [String, Number],
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'password'].includes(value)
    },
    placeholder: {
      type: String,
      default: ''
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
    formatter: {
      type: [Function, Object],
      default: null
    },
    unformattedOnFocus: {
      type: Boolean,
      default: false
    },
    mask: {
      type: [String, Function, Boolean],
      default: null
    },
    maskPattern: {
      type: RegExp,
      default: null
    },
    errorMessages: {
      type: Array,
      default: () => [],
    },
    limit: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      internalValue: this.value,
      rawValue: this.value,
      isFocused: false,
      lastInputValue: this.value,
      showPassword: false
    };
  },
  computed: {
    inputClasses() {
      return clsx('input-text', {
        'input-text--disabled': this.isDisabled,
        'input-text--has-error': this.hasError,
        'input-text--password': this.type === 'password'
      });
    },
    computedType() {
      if (this.type === 'password') {
        return this.showPassword ? 'text' : 'password';
      }
      return this.type;
    },
    displayValue() {
      if (this.unformattedOnFocus && this.isFocused) {
        return this.rawValue;
      }
      if (this.formatter) {
        if (typeof this.formatter === 'function') {
          return this.formatter(this.rawValue);
        } else if (this.formatter.format) {
          return this.formatter.format(this.rawValue);
        }
      }
      return this.rawValue;
    },
    hasError() {
      return this.errorMessages?.length > 0;
    }
  },
  created() {
    this.rawValue = this.value;
    this.internalValue = this.value;
  },
  methods: {
    handleInput(event) {
      this.errorMessage = [];
      let rawInput = event.target.value;

      if (this.mask) {
        rawInput = this.applyMask(rawInput);
        event.target.value = this.getDisplayValue(rawInput);
      }

      if (this.formatter && typeof this.formatter === 'object' && this.formatter.revert) {
        rawInput = this.formatter.revert(rawInput);
      }

      this.rawValue = rawInput;
      this.internalValue = rawInput;
      this.$emit('input', this.rawValue);
    },
    handleChange() {
      this.$emit('change', this.rawValue);
    },
    getDisplayValue(value) {
      if (this.formatter) {
        if (typeof this.formatter === 'function') {
          return this.formatter(value);
        } else if (this.formatter.format) {
          return this.formatter.format(value);
        }
      }
      return value;
    },
    applyMask(value) {
      if (!value) return value;

      if (typeof this.mask === 'function') {
        return this.mask(value);
      }

      if (this.mask === 'number') {
        return numberMask(value);
      }

      if (typeof this.mask === 'string') {
        return patternMask(value, this.mask);
      }

      return value;
    },
    handleFocus() {
      this.isFocused = true;
      this.$emit('focus');
    },
    handleBlur() {
      this.isFocused = false;
      this.$emit('blur', this.rawValue);
    },
    togglePasswordVisibility() {
      this.showPassword = !this.showPassword;
    },
    focus() {
      this.$refs.input.focus();
    },
    getRawValue() {
      return this.rawValue;
    },
  },
  watch: {
    value(newValue) {
      this.rawValue = newValue;
      this.internalValue = newValue;
    }
  },
};
</script>

<style lang="scss" scoped>
.input-text-wrapper {
  width: 100%;
}

.input-text-container {
  position: relative;
  width: 100%;
}

.input-text {
  background-color: #ffffff;
  border: 1px solid #E4E4E7;
  border-radius: 6px;
  font-family: 'Geist', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: #09090B;
  padding: 8px 12px;
  margin-bottom: 4px;
  width: 100%;

  &::placeholder {
    color: #D4D4D8;
  }

  &--password {
    padding-right: 40px;
  }

  &--disabled {
    background-color: #F6F6F9;
    opacity: 0.6;
    cursor: not-allowed;
  }

  &--has-error {
    border-color: #f00;
  }

  &--error {
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    color: #f00;
    margin-top: 4px;
    margin-bottom: 12px;
  }
}

.input-text__toggle {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #71717A;
  transition: color 0.2s;

  &:hover:not(:disabled) {
    color: #09090B;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    display: block;
  }
}
</style>
